// /src/ItemSelector.js
import React, { useState, useEffect } from "react";
import generatePDF from "./PDFGenerator";
import api from "./services/api";
import UnitConverter from "./UnitConverter";
import SafeListboxPortal from "./SafeListbox"; // Make sure file name matches (SafeListbox.js default export)

const ItemSelector = () => {
  const [customer, setCustomer] = useState({
    name: "",
    city: "",
    mobile: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [discountAmount, setDiscountAmount] = useState(0);
  const [includeGst, setIncludeGst] = useState(false);

  // categories now come from API
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [customItems, setCustomItems] = useState([]);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState(null); // id of the quotation already saved from this form
  const [error, setError] = useState(false);

  // Modal State
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [quotationNo, setQuotationNo] = useState(null);
  const [clonedFromNo, setClonedFromNo] = useState(null);

  // Direct WhatsApp State
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [waFeedback, setWaFeedback] = useState("");

  const [loadingItems, setLoadingItems] = useState(true);
  const [itemsError, setItemsError] = useState("");

  // 🔹 Load items/categories from backend (with optional clone support)
  useEffect(() => {
    const initPage = async () => {
      try {
        setLoadingItems(true);
        setItemsError("");

        const searchParams = new URLSearchParams(window.location.search);
        const cloneId = searchParams.get("clone");

        const requests = [api.get(`/quotations/items/categories`)];
        if (cloneId) {
          requests.push(api.get(`/quotations/${cloneId}`));
        }

        const [categoriesRes, cloneRes] = await Promise.all(requests);
        const categoriesData = categoriesRes.data || [];
        setCategories(categoriesData);

        if (cloneRes && cloneRes.data) {
          const q = cloneRes.data;
          setClonedFromNo(q.quotation_no || cloneId);

          setCustomer({
            name: q.customer_name || q.customer?.name || "",
            city: q.city || q.customer?.city || "",
            mobile: q.mobile || q.customer?.mobile || "",
            date: new Date().toISOString().split("T")[0], // Always set new quote date to today!
          });

          // Discount
          let loadedDiscount = 0;
          if (q.discount_amount !== undefined && q.discount_amount !== null) {
            loadedDiscount = parseFloat(q.discount_amount);
          } else if (q.discount_percent) {
            const total = parseFloat(q.total || 0);
            loadedDiscount = (total * parseFloat(q.discount_percent)) / 100;
          }
          setDiscountAmount(Number(loadedDiscount || 0));

          // GST
          if (q.include_gst !== undefined && q.include_gst !== null) {
            setIncludeGst(q.include_gst === 1 || q.include_gst === true || q.include_gst === "true");
          }

          // Map items
          const fixed = categoriesData.map(() => ({ name: "", price: 0, quantity: "", description: "" }));
          const customs = [];
          const itemsArray = Array.isArray(q.items) ? q.items : [];

          itemsArray.forEach((it) => {
            const catIndex = categoriesData.findIndex((c) => c.category === it.category);
            if (catIndex >= 0) {
              fixed[catIndex] = {
                name: it.name || "",
                price: Number(it.price || 0),
                quantity: String(it.quantity || ""),
                description: it.description || "",
              };
            } else {
              customs.push({
                name: it.name || "",
                price: String(it.price || ""),
                quantity: String(it.quantity || ""),
                description: it.description || "",
              });
            }
          });

          setSelectedItems(fixed);
          setCustomItems(customs);
        } else {
          // Normal initialize
          setSelectedItems(
            categoriesData.map(() => ({
              name: "",
              price: 0,
              quantity: "",
              description: "",
            }))
          );
        }
      } catch (err) {
        console.error("Error loading categories/clone data:", err);
        setItemsError("Failed to load items. Please refresh the page.");
      } finally {
        setLoadingItems(false);
      }
    };

    initPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // category names for PDF + payload
  const categoryNames = categories.map((c) => c.category);

  // Direct WhatsApp Sender
  const handleSendDirectWhatsApp = async () => {
    try {
      setSendingWhatsApp(true);
      setWaFeedback("");

      const pdfBase64 = generatePDF({
        customer: { ...customer, quotationNo },
        selectedItems,
        categoryNames,
        customItems,
        total,
        discountAmount: finalDiscountAmount,
        grandTotal,
        includeGst,
      }, "base64");

      const res = await api.post("/whatsapp/send-quotation", {
        mobile: customer.mobile,
        pdfBase64,
        quotationNo,
        customerName: customer.name,
        grandTotal,
      });

      setWaFeedback(res.data?.message || "Quotation PDF delivered successfully on WhatsApp!");
    } catch (err) {
      console.error(err);
      const errMsg =
        err.response?.data?.error ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Failed to send PDF on WhatsApp. Ensure WhatsApp is connected in Navbar.";
      setWaFeedback("❌ " + errMsg);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // handlers
  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      setCustomer({ ...customer, [name]: value.replace(/[^0-9]/g, "") });
    } else {
      setCustomer({ ...customer, [name]: value });
    }
  };

  const handleSelect = (index, selectedName, fillSet = false) => {
    if (!selectedName) {
      setSelectedItems((prev) => {
        const next = [...prev];
        next[index] = { name: "", price: 0, quantity: "", description: "" };
        return next;
      });
      return;
    }

    const selectedItem = categories[index]?.items.find(
      (item) => item.name === selectedName
    );

    // Only the "Full Set" dropdown auto-fills the matching accessories (nipple,
    // clamp, bore cap, joto) of the same inch size; a plain pipe pick stays alone.
    const pipeMatch = fillSet ? /^PIPE\s+([\d.]+)\s*inch/i.exec(selectedName) : null;
    const setSize = pipeMatch ? pipeMatch[1] : null;
    const accessoryQty = { NIPPLE: "1", CLAMP: "2", "BORE CAP": "1", JOTO: "1" };

    setSelectedItems((prev) => {
      const next = [...prev];
      next[index] = {
        name: selectedItem?.name || "",
        price: selectedItem?.price || 0,
        quantity: setSize && prev[index]?.name ? prev[index].quantity : "",
        description: "",
      };

      if (setSize) {
        categories.forEach((cat, ci) => {
          if (ci === index) return;
          const accKey = Object.keys(accessoryQty).find((k) =>
            cat.items.some((it) => it.name.toUpperCase().startsWith(k))
          );
          if (!accKey) return;
          const match = cat.items.find((it) => {
            const m = /([\d.]+)\s*inch\s*$/i.exec(it.name);
            return it.name.toUpperCase().startsWith(accKey) && m && parseFloat(m[1]) === parseFloat(setSize);
          });
          if (match) {
            next[ci] = {
              name: match.name,
              price: match.price || 0,
              quantity: next[ci]?.name && next[ci].quantity ? next[ci].quantity : accessoryQty[accKey],
              description: next[ci]?.description || "",
            };
          }
        });
      }
      return next;
    });
  };

  // Full Set: sizes available are read from the PIPE items in the catalog
  const pipeCategoryIndex = categories.findIndex((c) =>
    c.items.some((it) => /^PIPE\s+[\d.]+\s*inch/i.test(it.name))
  );

  const fullSetSizes =
    pipeCategoryIndex >= 0
      ? categories[pipeCategoryIndex].items
          .map((it) => /^PIPE\s+([\d.]+)\s*inch/i.exec(it.name))
          .filter(Boolean)
          .map((m) => m[1])
          .filter((s, i, arr) => arr.indexOf(s) === i)
      : [];

  // dropdown mirrors the currently selected pipe, so it never goes stale
  const fullSetSize =
    (/^PIPE\s+([\d.]+)\s*inch/i.exec(selectedItems[pipeCategoryIndex]?.name || "") || [])[1] || "";

  const handleFullSet = (size) => {
    if (pipeCategoryIndex < 0) return;
    if (!size) {
      // remove the whole set: pipe + nipple, clamp, bore cap, joto
      const setPrefixes = ["PIPE", "NIPPLE", "CLAMP", "BORE CAP", "JOTO"];
      setSelectedItems((prev) =>
        prev.map((sel, ci) =>
          categories[ci]?.items.some((it) =>
            setPrefixes.some((p) => it.name.toUpperCase().startsWith(p))
          )
            ? { name: "", price: 0, quantity: "", description: "" }
            : sel
        )
      );
      return;
    }
    const pipe = categories[pipeCategoryIndex].items.find((it) => {
      const m = /^PIPE\s+([\d.]+)\s*inch/i.exec(it.name);
      return m && parseFloat(m[1]) === parseFloat(size);
    });
    if (pipe) handleSelect(pipeCategoryIndex, pipe.name, true);
  };

  const handleQuantityChange = (index, event) => {
    const quantity = event.target.value.replace(/[^0-9]/g, "");
    setSelectedItems((prev) => {
      const next = [...prev];
      if (!next[index]) {
        next[index] = { name: "", price: 0, quantity: "", description: "" };
      }
      next[index].quantity = quantity;
      return next;
    });
  };

  const handleDescriptionChange = (index, event) => {
    const description = event.target.value;
    setSelectedItems((prev) => {
      const next = [...prev];
      if (!next[index]) {
        next[index] = { name: "", price: 0, quantity: "", description: "" };
      }
      next[index].description = description;
      return next;
    });
  };

  const addCustomItem = () => {
    setCustomItems((prev) => [
      ...prev,
      { name: "", price: "", quantity: "", description: "" },
    ]);
  };

  const updateCustomItem = (idx, field, value) => {
    setCustomItems((prev) => {
      const next = [...prev];
      if (field === "price") {
        next[idx][field] = value.replace(/[^0-9.]/g, "");
      } else if (field === "quantity") {
        next[idx][field] = value.replace(/[^0-9]/g, "");
      } else {
        next[idx][field] = value;
      }
      return next;
    });
  };

  const removeCustomItem = (idx) => {
    setCustomItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const fixedTotal = selectedItems.reduce(
    (sum, item) =>
      sum + (item?.quantity ? item.price * parseInt(item.quantity, 10) : 0),
    0
  );

  const customTotal = customItems.reduce((sum, item) => {
    const qty = parseInt(item.quantity || "0", 10);
    const price = parseFloat(item.price || "0");
    return sum + (qty > 0 && price > 0 ? qty * price : 0);
  }, 0);

  const total = fixedTotal + customTotal;

  const finalDiscountAmount = parseFloat(discountAmount || 0);

  const grandTotal = Math.max(0, total - finalDiscountAmount);

  const isCustomerValid =
    customer.name.trim() !== "" &&
    customer.city.trim() !== "" &&
    customer.mobile.trim() !== "";

  const buildItemsPayload = () => {
    const fixed = selectedItems
      .map(
        (item, index) =>
          item?.quantity > 0 &&
          item.name &&
          categories[index] && {
            category: categoryNames[index] || categories[index].category,
            name: item.name,
            quantity: parseInt(item.quantity, 10),
            price: item.price,
            total: item.price * parseInt(item.quantity, 10),
            description: item.description || ""
          }
      )
      .filter(Boolean);

    const custom = customItems
      .map((ci) => {
        const qty = parseInt(ci.quantity || "0", 10);
        const price = parseFloat(ci.price || "0");
        if (!ci.name?.trim() || qty <= 0 || price <= 0) return null;
        return {
          category: "Custom",
          name: ci.name.trim(),
          quantity: qty,
          price: price,
          total: qty * price,
          description: ci.description || ""
        };
      })
      .filter(Boolean);

    return [...fixed, ...custom];
  };

  // Any edit after saving re-enables the Save button
  useEffect(() => {
    setSaved(false);
  }, [customer, selectedItems, customItems, discountAmount, includeGst]);

  const saveQuotationToBackend = async () => {
    if (!isCustomerValid) return;

    setSaving(true);
    setSaved(false);
    setError(false);

    try {
      const updatedCustomer = { ...customer };
      const items = buildItemsPayload();

      const payload = {
        customer: updatedCustomer,
        items,
        discountAmount: finalDiscountAmount,
        total: parseFloat(total.toFixed(2)),
        netTotal: parseFloat(grandTotal.toFixed(2)),
        includeGst,
      };

      // Already saved from this form -> update it instead of creating a duplicate
      const saveRes = savedId
        ? await api.put(`/quotations/update/${savedId}`, payload)
        : await api.post(`/quotations/save-quotation`, payload);
      if (!savedId && saveRes.data?.id) setSavedId(saveRes.data.id);

      setSaving(false);
      setSaved(true);
      setError(false);
      
      // TRIGGER MODAL
      if (saveRes.data && saveRes.data.quotation_no) {
          setQuotationNo(saveRes.data.quotation_no);
      }
      setShowSuccessModal(true);

    } catch (err) {
      setSaving(false);
      setSaved(false);
      setError(true);
      console.error("❌ Save Error:", err);
    }
  };

  const rupee = (v) => {
    const n =
      typeof v === "string"
        ? parseFloat(v)
        : v;

    return Number.isFinite(n)
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 })
      : "0";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white pb-20">
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="orb absolute w-[500px] h-[500px] -top-20 -left-20 bg-purple-600/20 rounded-full blur-[100px]"></div>
        <div className="orb absolute w-[400px] h-[400px] bottom-0 right-0 bg-indigo-600/20 rounded-full blur-[100px]" style={{ animationDelay: "-5s" }}></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 relative z-10 max-w-7xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-200 via-pink-200 to-indigo-200 inline-block">
            Create New Quotation
          </h1>
          <p className="text-purple-300/60 mt-2 text-sm uppercase tracking-widest font-medium">Fast & Professional Estimates</p>
          
          <div className="mt-5 flex flex-wrap justify-center items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30">
              <span className="text-sm font-bold text-emerald-300">Full Set</span>
              <select
                value={fullSetSize}
                onChange={(e) => handleFullSet(e.target.value)}
                disabled={fullSetSizes.length === 0}
                className="bg-slate-900 text-white text-sm rounded-lg px-2 py-1 border border-white/10 focus:outline-none"
              >
                <option value="">{fullSetSize ? "✕ Remove set" : "Select size…"}</option>
                {fullSetSizes.map((s) => (
                  <option key={s} value={s}>{s} inch</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => handleFullSet("")}
                title="Remove pipe, nipple, clamp, bore cap and joto"
                className="text-xs font-bold text-red-300 hover:text-white px-2 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/30 border border-red-500/20 transition-colors"
              >
                Clear set
              </button>
            </div>
          </div>
        </div>

        {clonedFromNo && (
          <div className="mb-6 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center justify-between animate-fade-in-up">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">
                <i className="fas fa-clone"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-white">Duplicating from #{clonedFromNo}</p>
                <p className="text-xs text-emerald-300/70">All customer info and items loaded. Saving will generate a brand new quotation number.</p>
              </div>
            </div>
            <button
              onClick={() => {
                window.history.replaceState({}, document.title, window.location.pathname);
                window.location.reload();
              }}
              className="text-xs text-white/50 hover:text-white px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              Clear / Start Fresh
            </button>
          </div>
        )}

        <div className="glass-panel p-6 md:p-8 rounded-3xl border border-white/10 shadow-2xl backdrop-blur-xl bg-white/5">
          
          {/* Customer Details Section */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8">
            <div className="md:col-span-4">
                 <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">Customer Name</label>
                 <input
                    name="name"
                    placeholder="Enter full name"
                    value={customer.name}
                    onChange={handleCustomerChange}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all font-medium"
                 />
            </div>
            <div className="md:col-span-3">
                 <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">City</label>
                 <input
                    name="city"
                    placeholder="City / Location"
                    value={customer.city}
                    onChange={handleCustomerChange}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                 />
            </div>
            <div className="md:col-span-3">
                 <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">Mobile</label>
                 <input
                    name="mobile"
                    placeholder="10-digit number"
                    value={customer.mobile}
                    onChange={handleCustomerChange}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all font-mono"
                 />
            </div>
            <div className="md:col-span-2">
                 <label className="block text-xs font-bold text-purple-300 uppercase tracking-wider mb-1 ml-1">Date</label>
                 <input
                    name="date"
                    type="date"
                    value={customer.date}
                    onChange={handleCustomerChange}
                    className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all cursor-pointer"
                 />
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-8"></div>

          {loadingItems && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-purple-500/30 border-t-purple-400 rounded-full animate-spin"></div>
              <p className="mt-4 text-purple-300 text-sm font-medium animate-pulse">Loading amazing items...</p>
            </div>
          )}

          {itemsError && !loadingItems && (
            <div className="text-center p-8 bg-red-500/10 rounded-2xl border border-red-500/20">
              <p className="text-red-400 font-medium">{itemsError}</p>
            </div>
          )}

          {!loadingItems && !itemsError && (
            <>
              {/* Product Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
                {categories.map((category, i) => (
                  <div
                    key={category.category + i}
                    className="group relative bg-slate-900/40 rounded-2xl p-5 border border-white/5 hover:border-purple-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-purple-900/10"
                  >
                    <h3 className="text-lg font-bold text-purple-100 mb-4 flex items-center justify-between">
                      {category.category}
                      {selectedItems[i]?.quantity > 0 && (
                          <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full border border-emerald-500/20 font-bold">
                              Added
                          </span>
                      )}
                    </h3>

                    <div className="flex flex-col gap-4">
                        {/* Selector */}
                        <div className="relative z-20">
                             <SafeListboxPortal
                                items={category.items} // pass the array of objects {name, price}
                                value={selectedItems[i]?.name || ""}
                                onChange={(val) => handleSelect(i, val)}
                                placeholder="Choose model..."
                                buttonClassName="w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-left text-sm text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                                optionsClassName="max-h-60 overflow-y-auto bg-slate-900 border border-white/10 rounded-xl shadow-2xl py-1"
                                renderLabel={(item) => item.name}
                                renderMeta={(item) => `₹${rupee(includeGst ? item.price / 1.18 : item.price)}`}
                             />
                        </div>

                        {/* Description Input */}
                        <div className="relative z-10">
                            <input
                                type="text"
                                value={selectedItems[i]?.description || ""}
                                onChange={(e) => handleDescriptionChange(i, e)}
                                placeholder="Add description (optional)..."
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500/40 placeholder-white/30"
                                disabled={!selectedItems[i]?.name}
                            />
                        </div>

                        {/* Qty & Price Row */}
                        <div className="flex items-center gap-3">
                           <div className="relative w-24">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={selectedItems[i]?.quantity || ""}
                                    onChange={(e) => handleQuantityChange(i, e)}
                                    placeholder="Qty"
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-center text-white font-bold focus:outline-none focus:ring-2 focus:ring-purple-500/40 placeholder-white/20"
                                    disabled={!selectedItems[i]?.name}
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white/30 uppercase font-bold pointer-events-none">
                                    NOS
                                </span>
                           </div>
                           
                           <div className="flex-1 bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border border-emerald-500/20 rounded-xl px-4 py-3 flex items-center justify-between">
                                <span className="text-xs text-emerald-400/70 font-bold uppercase tracking-wider">Total</span>
                                <span className="text-lg font-bold text-emerald-400 font-mono">
                                    ₹{selectedItems[i]?.quantity
                                        ? rupee((includeGst ? selectedItems[i].price / 1.18 : selectedItems[i].price) * parseInt(selectedItems[i].quantity || 0, 10))
                                        : "0"}
                                </span>
                           </div>
                        </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Custom Items Section */}
              <div className="mb-8 bg-slate-900/30 rounded-2xl p-6 border border-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-purple-200 font-bold flex items-center gap-2">
                    <i className="fas fa-tools text-purple-400"></i> Custom / Ad-hoc Items
                  </h3>
                  <button
                    type="button"
                    onClick={addCustomItem}
                    className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-bold border border-white/10 transition-all flex items-center gap-2"
                  >
                    <i className="fas fa-plus"></i> Add Item
                  </button>
                </div>

                {customItems.length === 0 ? (
                  <div className="text-center py-6 text-white/20 italic text-sm border-2 border-dashed border-white/5 rounded-xl">
                    No custom items added. Click above to add extra services or parts.
                  </div>
                ) : (
                    <div className="space-y-3">
                        {customItems.map((ci, idx) => (
                        <div
                            key={idx}
                            className="grid grid-cols-12 gap-3 items-center bg-white/5 p-3 rounded-xl border border-white/5 animate-[fadeIn_0.3s_ease-out]"
                        >
                            <div className="col-span-12 md:col-span-5 flex flex-col gap-2">
                                <input
                                type="text"
                                placeholder="Item Name"
                                value={ci.name}
                                onChange={(e) => updateCustomItem(idx, "name", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-white placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                                <input
                                type="text"
                                placeholder="Add description (optional)..."
                                value={ci.description || ""}
                                onChange={(e) => updateCustomItem(idx, "description", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-sm text-white/70 placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                            </div>
                            <div className="col-span-3 md:col-span-2">
                                <input
                                type="text"
                                placeholder="Qty"
                                value={ci.quantity}
                                onChange={(e) => updateCustomItem(idx, "quantity", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-center text-white placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <input
                                type="text"
                                placeholder="Price"
                                value={ci.price}
                                onChange={(e) => updateCustomItem(idx, "price", e.target.value)}
                                className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-center text-white placeholder-white/20 focus:outline-none focus:border-purple-400 transition-colors"
                                />
                            </div>
                            <div className="col-span-4 md:col-span-2 text-right font-mono text-emerald-400 font-bold text-sm md:text-base">
                                ₹{rupee(((includeGst ? parseFloat(ci.price || 0) / 1.18 : parseFloat(ci.price || 0)) * parseInt(ci.quantity || 0, 10)) || 0)}
                            </div>
                            <div className="col-span-1 text-right">
                                <button
                                    onClick={() => removeCustomItem(idx)}
                                    className="text-white/40 hover:text-red-400 transition-colors"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                        ))}
                    </div>
                )}
              </div>
            </>
          )}

          {/* Unit Converter Widget */}
          <UnitConverter />

          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-8"></div>

          {/* Grand Total Area */}
          <div className="flex justify-end mb-4 pr-2">
            <label className="flex items-center cursor-pointer gap-3 bg-slate-900/50 px-4 py-2 rounded-xl border border-white/10 hover:bg-slate-900/80 transition-colors">
              <span className="text-sm font-bold text-purple-300 uppercase tracking-widest">
                Bill With GST
              </span>
              <div className="relative">
                <input 
                  type="checkbox" 
                  className="sr-only" 
                  checked={includeGst}
                  onChange={(e) => setIncludeGst(e.target.checked)}
                />
                <div className={`block w-10 h-6 rounded-full transition-colors ${includeGst ? 'bg-emerald-500' : 'bg-slate-700'}`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${includeGst ? 'transform translate-x-4' : ''}`}></div>
              </div>
            </label>
          </div>
          
          <div className="bg-gradient-to-r from-slate-900 to-slate-900 border border-white/10 rounded-3xl p-8 relative overflow-hidden">
             <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 blur-[80px] rounded-full pointer-events-none"></div>
             
             <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                 
                 {/* Discount Input */}
                 <div className="flex flex-col gap-2">
                     <label className="text-sm font-bold text-white/50 uppercase tracking-widest">Discount Amount (₹)</label>
                     <div className="relative">
                        <input
                            type="number"
                            value={discountAmount}
                            onChange={(e) => setDiscountAmount(e.target.value)}
                            placeholder="0"
                            className="w-40 bg-white/5 border border-white/10 rounded-xl pl-8 pr-4 py-3 text-xl font-bold text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all text-right"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 font-bold">₹</span>
                     </div>
                 </div>

                 {/* Final Total */}
                 <div className="text-center md:text-right">
                     {includeGst ? (
                         <div className="flex flex-col items-end gap-1 mb-4 text-sm font-mono">
                             <div className="flex justify-between w-52 text-white/70">
                                 <span>Taxable Amount:</span>
                                 <span>₹{rupee(grandTotal / 1.18)}</span>
                             </div>
                             <div className="flex justify-between w-52 text-white/70">
                                 <span>CGST (9%):</span>
                                 <span>₹{rupee((grandTotal - (grandTotal / 1.18)) / 2)}</span>
                             </div>
                             <div className="flex justify-between w-52 text-white/70">
                                 <span>SGST (9%):</span>
                                 <span>₹{rupee((grandTotal - (grandTotal / 1.18)) / 2)}</span>
                             </div>
                         </div>
                     ) : (
                         <div className="flex flex-col items-end gap-1 mb-4 text-sm font-mono">
                             <div className="flex justify-between w-52 text-white/70">
                                 <span>Total Amount:</span>
                                 <span>₹{rupee(total)}</span>
                             </div>
                             {finalDiscountAmount > 0 && (
                                 <div className="flex justify-between w-52 text-white/70 text-red-400">
                                     <span>Discount:</span>
                                     <span>- ₹{rupee(finalDiscountAmount)}</span>
                                 </div>
                             )}
                         </div>
                     )}
                     <div className="text-sm font-bold text-emerald-400 uppercase tracking-widest mb-1">Net Payable Amount</div>
                     <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                        ₹{rupee(Math.round(grandTotal))}
                     </div>
                     <div className="text-white/30 text-sm mt-2 font-medium">
                        Total Items: {buildItemsPayload().length}
                     </div>
                 </div>
             </div>
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <button
               onClick={() =>
                generatePDF({
                  customer,
                  selectedItems,
                  categoryNames,
                  customItems,
                  total,
                  discountAmount: finalDiscountAmount,
                  grandTotal,
                  includeGst,
                })
              }
              disabled={!isCustomerValid}
              className={`group flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg shadow-lg border border-white/10 transition-all ${
                !isCustomerValid 
                ? "bg-white/5 text-white/30 cursor-not-allowed" 
                : "bg-white/10 hover:bg-white/20 text-white hover:scale-[1.02]"
              }`}
            >
              <i className="fas fa-file-pdf text-purple-400 group-hover:text-purple-300"></i> 
              Download PDF
            </button>

            <button
              onClick={() => {
                window.open(
                  generatePDF({
                    customer,
                    selectedItems,
                    categoryNames,
                    customItems,
                    total,
                    discountAmount: finalDiscountAmount,
                    grandTotal,
                    includeGst,
                  }, "preview"),
                  "_blank"
                );
              }}
              disabled={!isCustomerValid}
              className={`group flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg shadow-lg border border-white/10 transition-all ${
                !isCustomerValid
                ? "bg-white/5 text-white/30 cursor-not-allowed"
                : "bg-white/10 hover:bg-white/20 text-white hover:scale-[1.02]"
              }`}
            >
              <i className="fas fa-eye text-cyan-400 group-hover:text-cyan-300"></i>
              Preview
            </button>

            <button
              onClick={saveQuotationToBackend}
              disabled={!isCustomerValid || saving || saved}
              className={`flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-indigo-900/30 transition-all ${
                 !isCustomerValid || saving || saved
                 ? "bg-indigo-600/50 text-white/50 cursor-not-allowed"
                 : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white hover:scale-[1.02]"
              }`}
            >
              {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                  <i className="fas fa-save"></i>
              )}
              {saving ? "Saving..." : saved ? "Saved!" : "Save Quotation"}
            </button>
          </div>

          {error && (
            <div className="mt-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-center">
               <p className="text-red-200 font-bold">❌ Failed to save quotation. Please try again.</p>
            </div>
          )}

        </div>
        
        {/* Footer Text */}
        <div className="mt-8 text-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            
        </div>
      </div>

       {/* SUCCESS MODAL */}
       {showSuccessModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.2s_ease-out]">
             <div className="bg-[#0f172a] border border-white/10 rounded-3xl p-8 max-w-md w-full text-center shadow-2xl relative">
                
                 {/* Success Icon */}
                 <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-emerald-500 to-emerald-400 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
                    <i className="fas fa-check text-3xl text-white"></i>
                 </div>

                 <h2 className="text-2xl font-bold text-white mb-2">Quotation Saved!</h2>
                 <div className="bg-white/5 rounded-xl p-3 mb-6 border border-white/5">
                    <p className="text-purple-200 text-sm">Quotation Number</p>
                    <p className="text-xl font-mono text-emerald-400 font-bold tracking-wider">{quotationNo || "---"}</p>
                 </div>

                 <div className="flex flex-col gap-3">
                     <button
                        onClick={() => {
                             generatePDF({
                                customer: { ...customer, quotationNo }, 
                                selectedItems,
                                categoryNames,
                                customItems,
                                total,
                                discountAmount: finalDiscountAmount,
                                grandTotal,
                                includeGst,
                              }, "download"); // Default action is download
                        }}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors shadow-lg shadow-indigo-900/20"
                     >
                         <i className="fas fa-file-download text-lg"></i> Download PDF
                     </button>

                     <button
                        onClick={() => {
                             generatePDF({
                                customer: { ...customer, quotationNo }, 
                                selectedItems,
                                categoryNames,
                                customItems,
                                total,
                                discountAmount: finalDiscountAmount,
                                grandTotal,
                                includeGst,
                              }, "print");
                        }}
                        className="w-full bg-white/10 hover:bg-white/15 text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors border border-white/5"
                     >
                         <i className="fas fa-print text-purple-400"></i> Print Quotation
                     </button>
                     
                      {/* Direct WhatsApp PDF Delivery */}
                     <button
                        onClick={handleSendDirectWhatsApp}
                        disabled={sendingWhatsApp}
                        className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-900/30"
                     >
                        {sendingWhatsApp ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Sending PDF to WhatsApp...</span>
                          </>
                        ) : (
                          <>
                            <i className="fab fa-whatsapp text-xl"></i>
                            <span>⚡ Send PDF to Customer's WhatsApp</span>
                          </>
                        )}
                     </button>

                     {waFeedback && (
                       <div className={`p-3 rounded-xl text-xs font-bold text-center ${waFeedback.startsWith("❌") ? "bg-red-500/20 text-red-300 border border-red-500/30" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 animate-pulse"}`}>
                         {waFeedback}
                       </div>
                     )}

                     {/* Fallback Web WhatsApp Link */}
                     <button
                        onClick={() => {
                          const phone = (customer.mobile || "").replace(/\D/g, "");
                          const message = encodeURIComponent(
                            `Hello ${customer.name}, here is your quotation #${quotationNo || 'Draft'}. Total Amount: ₹${grandTotal.toFixed(2)}`
                          );
                          window.open(`https://wa.me/91${phone}?text=${message}`, "_blank");
                        }}
                        className="w-full bg-white/5 hover:bg-white/10 text-white/60 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-colors border border-white/5"
                     >
                        <i className="fab fa-whatsapp"></i> Open in WhatsApp Web (Link Only)
                     </button>
                     
                     <div className="h-px bg-white/10 my-1"></div>

                     <button
                        onClick={() => window.location.reload()}
                        className="w-full text-white/50 hover:text-white py-3 rounded-xl font-bold transition-colors text-sm"
                     >
                        Create New Quotation
                     </button>
                     <button
                        onClick={() => setShowSuccessModal(false)}
                        className="absolute top-4 right-4 text-white/20 hover:text-white transition-colors"
                     >
                        <i className="fas fa-times text-xl"></i>
                     </button>
                 </div>
             </div>
        </div>
      )}

      {/* Global & Animation Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .orb {
          animation: float 10s ease-in-out infinite;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
        
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        ::-webkit-scrollbar-track {
          background: transparent; 
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1); 
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2); 
        }
      `}</style>
    </div>
  );
};

export default ItemSelector;
